import { useEffect, useState } from 'react';
import {
  Loader2,
  Star,
  X,
  Folder,
  FileText,
  Package,
  Tag,
  Flag,
  Heart,
  Bell,
  Bookmark,
  Archive,
  Truck,
  ShoppingCart,
  Users,
  Mail,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface FavoriteFolderModalProps {
  isOpen: boolean;
  folderName: string;
  initialColor?: string | null;
  initialIcon?: string | null;
  onSubmit: (data: { color: string | null; icon: string | null }) => Promise<void>;
  onClose: () => void;
}

// Zbiór ikon Lucide dostępnych do oznaczania ulubionych folderów.
// Klucz to nazwa eksportowana z Lucide — backend przechowuje string, renderer
// w sidebarze mapuje z powrotem na komponent.
const ICON_OPTIONS: Array<{ key: string; component: LucideIcon; label: string }> = [
  { key: 'Folder', component: Folder, label: 'Folder' },
  { key: 'FileText', component: FileText, label: 'Dokument' },
  { key: 'Package', component: Package, label: 'Paczka' },
  { key: 'Tag', component: Tag, label: 'Etykieta' },
  { key: 'Flag', component: Flag, label: 'Flaga' },
  { key: 'Heart', component: Heart, label: 'Serce' },
  { key: 'Bell', component: Bell, label: 'Dzwonek' },
  { key: 'Bookmark', component: Bookmark, label: 'Zakładka' },
  { key: 'Archive', component: Archive, label: 'Archiwum' },
  { key: 'Truck', component: Truck, label: 'Dostawa' },
  { key: 'ShoppingCart', component: ShoppingCart, label: 'Koszyk' },
  { key: 'Users', component: Users, label: 'Klienci' },
  { key: 'Mail', component: Mail, label: 'Mail' },
];

const COLOR_OPTIONS = [
  '#6B7280', // gray (default)
  '#FF6B9D', // neon-pink
  '#34D399', // neon-green
  '#22D3EE', // neon-cyan
  '#A78BFA', // purple
  '#FBBF24', // yellow
  '#F97316', // orange
  '#F87171', // red
  '#60A5FA', // blue
];

/**
 * Modal do konfiguracji prezentacji folderu w sekcji "Ulubione": kolor + ikona.
 * Dane są lokalne dla EcomFlow (pola InboxImapFolder.color/icon) — nie są propagowane na IMAP.
 */
export function FavoriteFolderModal({
  isOpen,
  folderName,
  initialColor = null,
  initialIcon = null,
  onSubmit,
  onClose,
}: FavoriteFolderModalProps) {
  const [color, setColor] = useState<string | null>(initialColor);
  const [icon, setIcon] = useState<string | null>(initialIcon);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setColor(initialColor);
      setIcon(initialIcon);
      setError(null);
    }
  }, [isOpen, initialColor, initialIcon]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ color, icon });
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || 'Nie udało się zapisać ulubionego folderu');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface-raised border border-border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-warning-bg flex items-center justify-center shrink-0">
            <Star className="w-5 h-5 text-warning fill-current" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text-primary">Ulubiony folder</h3>
            <p className="text-xs text-text-secondary mt-0.5 truncate">{folderName}</p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="btn-ghost p-1"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-text-secondary mb-2 block">Kolor akcentu</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c ? 'border-text-primary scale-110' : 'border-transparent hover:border-border-strong'
                  }`}
                  style={{ backgroundColor: c }}
                  aria-label={`Kolor ${c}`}
                />
              ))}
            </div>
          </div>
          {error && (
            <div className="text-xs text-danger bg-danger-bg border border-danger-border rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <label className="text-xs text-text-secondary mb-2 block">Ikona</label>
            <div className="grid grid-cols-7 gap-2">
              {ICON_OPTIONS.map(opt => {
                const Icon = opt.component;
                const isSelected = icon === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setIcon(opt.key)}
                    title={opt.label}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                      isSelected
                        ? 'bg-accent-primary/20 border border-accent-primary/40 text-accent-primary'
                        : 'bg-surface-hover/50 border border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-strong'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-border flex items-center gap-2">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
            Zapisz wygląd
          </button>
          <button
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary text-sm ml-auto"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper: mapuje string-key ikony (zapisany w `InboxImapFolder.icon`) na komponent Lucide.
 * Jeśli klucz nie istnieje lub jest null, zwraca domyślną ikonę Folder.
 */
export function resolveFolderIcon(key: string | null | undefined): LucideIcon {
  if (!key) return Folder;
  const match = ICON_OPTIONS.find(o => o.key === key);
  return match?.component || Folder;
}
